import { service } from '@ember/service';
import { cached } from '@glimmer/tracking';

import type { DateRangeInterface, DSSetup, RecordData, Service } from 'webapp-types';
import type { ScriptDetailItemType } from 'webapp/enums/enums';
import { ModelName } from 'webapp/enums/enums';
import { uniq } from 'webapp/utils/common';
import { getFormattedTime } from 'webapp/utils/number';
import type { ValidationRule } from 'webapp/utils/validate';
import type { VerificationInfo } from 'webapp/utils/verification';

import type { Metric, Source } from 'webapp/models-custom';
import BufferedModel from 'webapp/models-custom/buffer/buffered-model';
import MetricSetup from 'webapp/models-custom/metric-setup';
import type ScriptRecord from 'webapp/models/script';
import type TagRecord from 'webapp/models/tag';
import type UserRecord from 'webapp/models/user';

import type { TimezoneOption } from 'webapp/services/constants';
import { Severity } from 'webapp/services/logger';

interface ScriptOptions {
	name?: string;
	description?: string;
	metricDefinition?: MetricDefinition;
	created?: number;
	createdById?: number;
	code?: string;
	sourceId?: number;
	tags?: number[];
	originateFrom?: number;
}

export interface MetricDefinition {
	changeFormat: string;
	changeFunction: number;
	changeInvert: boolean;
	description: string;
	format: string;
	noReset: boolean;
}

export interface ExpressionDetailItemFunction {
	dateRange?: DateRangeInterface.FormulaInterface;
}

export interface ExpressionDetailItem {
	id: string;
	type: ScriptDetailItemType;
	value?: number; // only ScriptDetailItemType.CONSTANT
	metricSetup?: DSSetup.MetricSetupWithoutFiltersData; // only ScriptDetailItemType.METRIC
	function?: ExpressionDetailItemFunction; // only ScriptDetailItemType.METRIC (format of serialized data-select)
}

export default class Script extends BufferedModel<ScriptRecord, RecordData.Script.Serialized> {
	@service('logger') declare loggerService: Service.Logger;
	@service('popup') declare popupService: Service.Popup;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('constants') declare constantsService: Service.Constants;
	@service('model-manager/tags') declare mmTagsService: Service.ModelManager.Tags;
	@service('model-manager/users') declare mmUsersService: Service.ModelManager.Users;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;
	@service('model-manager/blocks') declare mmBlocksService: Service.ModelManager.Blocks;

	// ------- props from / to ED record ------

	get id(): string | null {
		return this.model?.id || null;
	}

	get name(): string {
		return this.getBuffered('name');
	}

	set name(value: string) {
		this.setBuffered('name', value);
	}

	get description(): string {
		return this.getBuffered('description');
	}

	set description(value: string) {
		this.setBuffered('description', value);
	}

	get metricDefinition(): MetricDefinition {
		return this.getBuffered('metricDefinition');
	}

	set metricDefinition(value: MetricDefinition) {
		this.setBuffered('metricDefinition', value);
	}

	get created(): number {
		return this.getBuffered('created');
	}

	set created(value: number) {
		this.setBuffered('created', value);
	}

	get createdById(): number {
		return this.getBuffered('createdById');
	}

	set createdById(value: number) {
		this.setBuffered('createdById', value);
	}

	get modified(): number | null {
		return this.model.modified;
	}

	get modifiedById(): number | null {
		return this.model.modifiedById;
	}

	get verificationInfo(): VerificationInfo | undefined {
		return this.metric?.verificationInfo;
	}

	get expression(): string {
		return this.getBuffered('expression');
	}

	set expression(value: string) {
		this.setBuffered('expression', value);
	}

	get expressionDetails(): ExpressionDetailItem[] {
		return this.getBuffered('expressionDetails');
	}

	set expressionDetails(value: ExpressionDetailItem[]) {
		this.setBuffered('expressionDetails', value);
	}

	get sourceId(): number {
		return this.getBuffered('sourceId');
	}

	set sourceId(value: number) {
		this.setBuffered('sourceId', value);
	}

	get tags(): number[] {
		return this.getBuffered('tags');
	}

	set tags(value: number[]) {
		this.setBuffered('tags', value);
	}

	get originateFrom(): number {
		return this.getBuffered('originateFrom');
	}

	set originateFrom(value: number) {
		this.setBuffered('originateFrom', value);
	}

	get timezone(): string | null {
		return this.getBuffered('timezone');
	}

	set timezone(value: string | null) {
		this.setBuffered('timezone', value);
	}

	get isMine(): boolean {
		return this.createdById === this.currentUserService.numericId;
	}

	// ------- other props ------

	override get validationRules(): Record<string, ValidationRule[]> {
		return {
			description: ['required'],
			expression: ['required'],
		};
	}

	get createdBy(): UserRecord | null {
		let user;
		if (this.createdById) {
			user = this.mmUsersService.findRecord(String(this.createdById));
			if (!user) {
				this.loggerService.logError(
					'Missing createdBy for script',
					{
						scriptId: this.id,
						userId: this.createdById,
					},
					Severity.WARNING,
				);
			}
		}
		return user || null;
	}

	get canEdit(): boolean {
		return (
			this.currentUserService.isAdmin ||
			String(this.createdById) === this.currentUserService.id
		);
	}

	get tagsObjs(): TagRecord[] {
		const tagIds = this.tags || [];
		return this.mmTagsService.savedRecords.filter((record: TagRecord) => {
			return tagIds.includes(+record.id!);
		});
	}

	@cached
	get metric(): Metric | null {
		let metric;
		if (this.numericId) {
			metric = this.outputMetrics[0];
		}
		return metric || null;
	}

	@cached
	get metricSetupWithoutFiltersDataArray(): DSSetup.MetricSetupWithoutFiltersData[] {
		const metricSetups: DSSetup.MetricSetupWithoutFiltersData[] = [];
		this.expressionDetails.map((i) => {
			if (i.metricSetup) {
				metricSetups.push(i.metricSetup);
			}
		});
		return metricSetups;
	}

	@cached
	get metricSetups(): MetricSetup[] {
		return this.metricSetupWithoutFiltersDataArray.map((msd) => {
			return new MetricSetup(msd);
		});
	}

	get tagsCount(): number {
		return this.tags.length;
	}

	@cached
	get usedTimezones() {
		const uniqueTimezones = uniq(this.usedSources.map((s) => s.timezone));
		const timezoneEntries: TimezoneOption[] = [];
		uniqueTimezones.forEach((timezone) => {
			const entry = this.constantsService.timezones.find((item) => item.value === timezone);
			if (entry) {
				timezoneEntries.push(entry);
			}
		});
		return timezoneEntries;
	}

	@cached
	get outputMetrics(): Metric[] {
		return this.mmMetricsService.getMetricsForScriptId(this.numericId!);
	}

	@cached
	get lastOutputMetric(): Metric | null {
		return [...this.outputMetrics].pop() || null;
	}

	get isFavorite(): boolean {
		return this.outputMetrics.some((m) => m.isFavorite);
	}

	getDataForClone(): ScriptOptions {
		const data = this.toJSON();
		data.description = `Copy of ${data.description}`;

		delete data.created;
		delete data.createdById;

		return data;
	}

	openCreateBlockModal(trackOrigin: string, createdNow: boolean): void {
		if (this.currentUserService.handleDemo()) {
			return;
		}

		const metric = this.lastOutputMetric;

		if (!metric) {
			return;
		}

		const metricSetup = new MetricSetup(metric.metricData || undefined);

		this.mmBlocksService.openCreateFromMetricSetupPopup({
			metricSetup,
			trackOrigin,
			createdNow,
		});
	}

	// ----------- Common with models-custom/custom-query -----------
	// used for filter-sort and list view for custom metrics

	get metricName(): string {
		return this.description;
	}

	set metricName(value: string) {
		this.description = value;
	}

	@cached
	get usedSources(): Source[] {
		const sources: Source[] = [];
		this.metricSetups.forEach((ms) => {
			const source = ms.metric?.source;
			if (source && !sources.find((s) => s.id === source.id)) {
				sources.push(source);
			}
		});
		return sources;
	}

	get usedSourceNames(): string {
		return this.usedSources.map((source) => source.title).join(' ') || '';
	}

	get usedSourceTypeKeys(): string {
		return this.usedSources.map((source) => source.sourceType?.key).join(' ') || '';
	}

	get createdByText(): string {
		return this.createdBy?.name || '/';
	}

	get createdTimeText(): string {
		return getFormattedTime(this.created, this.currentUserService.model?.timezone);
	}

	get modifiedBy(): UserRecord | null {
		if (this.modifiedById) {
			return this.mmUsersService.findRecord(String(this.modifiedById)) || null;
		}
		return null;
	}

	get modifiedByText(): string {
		return this.modifiedBy?.name || '/';
	}

	get modifiedTimeText(): string | null {
		return this.model.modified
			? getFormattedTime(this.model.modified, this.currentUserService.model?.timezone)
			: null;
	}

	@cached
	get usedMetricsText(): string {
		return uniq(this.metricSetups.map((ms) => ms.metric?.name)).join(', ');
	}

	// ----------- Static -----------

	static override modelName = ModelName.SCRIPT; // store record type
}
